import { type LucideIcon } from 'lucide-react';

import { cn } from '../lib/cn';

export type SegmentedOption<V extends string> = {
    value: V;
    label: string;
    icon?: LucideIcon;
    /** Optional count rendered as a small badge after the label. */
    count?: number;
};

type Props<V extends string> = {
    value: V;
    onChange: (value: V) => void;
    options: SegmentedOption<V>[];
    /**
     * `bar` (default): a connected iOS-style segmented control — one track, the
     * active segment is a raised pill. `pill`: independent rounded chips that
     * wrap (use when there are many options).
     */
    appearance?: 'bar' | 'pill';
    /** Stretch the bar to full width with equal-width segments (bar only). */
    block?: boolean;
    size?: 'md' | 'lg';
    ariaLabel?: string;
    className?: string;
};

const TRACK_PAD = { md: 'p-1', lg: 'p-1.5' } as const;
const SEG_SIZE = { md: 'h-10 px-4 text-sm', lg: 'h-12 px-5 text-base' } as const;
const PILL_SIZE = { md: 'h-10 px-4 text-sm', lg: 'h-12 px-5 text-base' } as const;

// Single-select control. `bar` renders a designed segmented track (the active
// segment slides up as a white/elevated pill); `pill` renders wrapping chips.
// Built big and high-contrast — a primary "which view am I in" control that a
// non-technical user can read at a glance and hit with a thumb.
export function Segmented<V extends string>({
    value,
    onChange,
    options,
    appearance = 'bar',
    block = false,
    size = 'lg',
    ariaLabel,
    className,
}: Props<V>) {
    if (appearance === 'pill') {
        return (
            <div role="group" aria-label={ariaLabel} className={cn('flex flex-wrap gap-2', className)}>
                {options.map((opt) => {
                    const Icon = opt.icon;
                    const selected = opt.value === value;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => onChange(opt.value)}
                            className={cn(
                                'inline-flex items-center gap-2 rounded-full border border-solid font-semibold transition-colors',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500',
                                PILL_SIZE[size],
                                selected
                                    ? 'border-blue-600 bg-blue-600 text-white shadow-sm dark:border-blue-500 dark:bg-blue-500'
                                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700',
                            )}
                        >
                            {Icon && <Icon aria-hidden="true" className="h-5 w-5 shrink-0" />}
                            <span className="whitespace-nowrap">{opt.label}</span>
                            <Count value={opt.count} selected={selected} />
                        </button>
                    );
                })}
            </div>
        );
    }

    return (
        <div
            role="group"
            aria-label={ariaLabel}
            className={cn(
                'inline-flex items-center gap-1 rounded-full border border-solid border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800/70',
                block && 'flex w-full',
                TRACK_PAD[size],
                className,
            )}
        >
            {options.map((opt) => {
                const Icon = opt.icon;
                const selected = opt.value === value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => onChange(opt.value)}
                        className={cn(
                            'inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-all',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-blue-500',
                            block && 'flex-1',
                            SEG_SIZE[size],
                            selected
                                ? 'bg-white text-blue-700 shadow-sm dark:bg-gray-700 dark:text-blue-300'
                                : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white',
                        )}
                    >
                        {Icon && <Icon aria-hidden="true" className="h-5 w-5 shrink-0" />}
                        <span className="whitespace-nowrap">{opt.label}</span>
                        <Count value={opt.count} selected={selected} />
                    </button>
                );
            })}
        </div>
    );
}

function Count({ value, selected }: { value?: number; selected: boolean }) {
    if (value == null) return null;
    return (
        <span
            className={cn(
                'ml-0.5 inline-flex min-w-[1.5rem] justify-center rounded-full px-1.5 py-0.5 text-xs font-bold',
                selected
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200'
                    : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
            )}
        >
            {value}
        </span>
    );
}
